/* ──────────────────────────────────────────────────────────────────────────────
   Faz 5 — Depozito İade İrsaliyeleri (backoffice liste)  ·  /deposit-returns

   ══ TUTAR KOLONU YOKTUR ═══════════════════════════════════════════════════════
   Depozito iadesi ticari bir işlem değil, EMANETİN GERİ VERİLMESİDİR. Belgede tutar
   hesaplanmaz; listede para gösteren TEK BİR KOLON BİLE YOKTUR (spec §0/§6, iş kuralı 4).
   `fmtMoney` bu dosyada BİLİNÇLİ OLARAK yoktur ve depositShared'dan da alınmaz.

   ══ KIRIK ORANI AYRICA İZLENİR (spec §8) ══════════════════════════════════════
   Kırık sepet de bakiyeden düşer — plasiyer onu geri getirmiştir. Ama sürekli yüksek
   kırık bildiren plasiyer ya bir TAŞIMA SORUNUNA ya da SUİSTİMALE işaret eder. Bu yüzden
   "Kırık %" kendi kolonudur, eşiği aşan satır kırmızıya boyanır ve tekrar eden plasiyerler
   listenin üstünde ayrı bir bantta toplanır — tek tek satırlara bakan kimse örüntüyü görmez.

   ══ LİSTE = c360 (useC360List) ════════════════════════════════════════════════
   Sayfalama/sıralama/kolon filtresi SUNUCUDA (OData). Bunun iki sonucu var ve ikisi de
   bilinçlidir:
     • Kapsam filtreleri (tarih · yüksek kırık) artık OData $filter cümlesidir (depositShared);
       client-side `Array.filter` 2. sayfayı göremezdi.
     • Kırmızı bant SAYFAYA DEĞİL, FİLTRENİN TAMAMINA bakar → ayrı ve yalın bir çekimle
       (fetchDamageStats) beslenir. Sayfaya bakan bir örüntü uyarısı, örüntüyü kaçırır.
   "Kırık %" TÜRETİLMİŞTİR (DB'de kolon yok) → sıralanamaz/filtrelenemez; kullanıcı onu
   "⚠ Yüksek kırık oranı" kısayoluyla daraltır.

   Bu ekran DepositCountsPage (/deposit-counts, SAYIM) ile karıştırılmamalıdır: o "araçta
   kaç sepet var" der ve stoğa dokunmaz; bu belge "şu sepetleri geri verdim" der ve düşürür.
   ────────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser, ViewIcon, buildSelectFields, unwrapLookupItems,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { DATE_PRESETS, fmtDate, fmtQty } from './returnShared';
import {
  RETURNS_API, statusLabel, statusTone, fmtPct, damageRate, documentDateOf,
  STATUS_SELECT_OPTIONS, DAMAGE_RATE_THRESHOLD,
  highDamageFilter, recentDaysFilter, fetchDamageStats, suspiciousRepsFrom,
} from './depositShared';

export const dst = {
  num: { textAlign: 'right' },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700 },
  danger: { color: 'var(--danger)', fontWeight: 800 },
  ok: { color: 'var(--success, #16a34a)', fontWeight: 700 },
  muted: { color: 'var(--text-secondary, #6b7280)' },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  filterInput: { ...s.input, height: 34, fontSize: 12, borderRadius: 6, width: 140 },
  filterSelect: { ...s.select, height: 34, fontSize: 12, borderRadius: 6, width: 160 },
  chip: {
    padding: '5px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer',
    border: '1px solid var(--border-color, rgba(15,23,42,0.14))', background: 'var(--bg-card, #fff)',
    color: 'var(--text-secondary, #6b7280)', userSelect: 'none',
  },
  chipDanger: { background: 'var(--danger)', color: '#fff', borderColor: 'transparent' },
  band: {
    display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px',
    background: 'var(--danger-soft, rgba(226,68,92,0.09))',
    border: '1px solid var(--danger)', borderRadius: 10, margin: '0 0 12px',
  },
  smallBtn: { ...s.btnSecondary, padding: '5px 9px', fontSize: 11, minWidth: 0, height: 'auto' },
};

export function DepositReturnsPage() {
  const navigate = useNavigate();
  usePageMeta('Depozito İade İrsaliyesi', 'Plasiyerin depozitolu ambalajı satıcıya geri götürdüğü belgeler. Tutar taşımaz.');

  const [toast, setToast] = useState(null);
  const [selected, setSelected] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [representatives, setRepresentatives] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  // Kapsam filtreleri (OData baseFilter'a çevrilir — kullanıcı huniyle temizleyemez).
  const [datePreset, setDatePreset] = useState('30');
  const [onlyHighDamage, setOnlyHighDamage] = useState(false);

  // Kırık örüntüsü bandı — listeden AYRI beslenir (bkz. dosya başı).
  const [stats, setStats] = useState([]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/representatives/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/warehouses/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
    ]).then(([r, w]) => {
      setRepresentatives(unwrapLookupItems(r));
      setWarehouses(unwrapLookupItems(w));
    }).catch(() => { /* lookup yoksa huniler boş açılır; liste yine çalışır */ });
  }, []);

  const repOptions = useMemo(
    () => representatives.map((r) => ({ value: r.id, label: r.name || r.textId || '(adsız)' })),
    [representatives],
  );
  // Araç = Warehouse (Type LIKE 'Araç%'). Ayrı bir vehicle tablosu YOKTUR.
  const vehicleOptions = useMemo(
    () => warehouses.map((w) => ({ value: w.id, label: w.name || w.textId || '(adsız)' })),
    [warehouses],
  );

  const repName = useCallback(
    (id) => representatives.find((x) => x.id === id)?.name || '-',
    [representatives],
  );
  const vehicleName = useCallback(
    (id) => {
      const w = warehouses.find((x) => x.id === id);
      return w ? (w.name || w.textId || '-') : '-';
    },
    [warehouses],
  );

  // Huniler: Durum · Plasiyer · Araç (spec §8 blok 3'ün filtreleri).
  const columnFilterMeta = useMemo(() => ({
    status: { type: 'select', odataField: 'Status', options: STATUS_SELECT_OPTIONS },
    representative: { type: 'select', odataField: 'RepresentativeId', valueFormat: 'guid', options: repOptions },
    vehicle: { type: 'select', odataField: 'WarehouseId', valueFormat: 'guid', options: vehicleOptions },
  }), [repOptions, vehicleOptions]);

  // Serbest arama: irsaliye no (tek string alan — int/guid'e contains yapılmaz).
  const searchFields = useMemo(() => ['DocumentNo'], []);

  const baseFilter = useMemo(() => {
    const parts = [];
    const dateClause = recentDaysFilter(datePreset);
    if (dateClause) parts.push(dateClause);
    if (onlyHighDamage) parts.push(highDamageFilter());
    return parts.join(' and ');
  }, [datePreset, onlyHighDamage]);

  // $select: hücrelerin okuduğu HER alan burada olmalı (Kırık % TotalQuantity/TotalDamaged'dan
  // türetilir; Tarih hücresi SentAt'i CreatedDate'e tercih eder → ikisi de gerekir).
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta,
    columnVisibility,
    always: [
      'Id', 'DocumentNo', 'RepresentativeId', 'WarehouseId',
      'TotalQuantity', 'TotalDamaged', 'Status', 'SentAt', 'CreatedDate',
    ],
  }), [columnVisibility, columnFilterMeta]);

  const list = useC360List({
    apiBase: RETURNS_API,
    baseFilter,
    columnFilterMeta,
    searchFields,
    defaultSortField: 'CreatedDate',
    defaultSortDir: 'desc',
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows, buildCurrentFilterParams,
  } = list;

  // ── Örüntü: TEK belgede yüksek kırık kaza olabilir; TEKRAR EDEN yüksek kırık olmaz.
  //    Listenin O ANKİ filtresiyle (buildCurrentFilterParams) aynı kümeyi toplulaştırır.
  const loadStats = useCallback(async () => {
    try {
      setStats(await fetchDamageStats(authHeaders(), buildCurrentFilterParams()));
    } catch {
      setStats([]); // bant kritik değil — listeyi engellemez
    }
  }, [buildCurrentFilterParams]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const refreshAll = useCallback(() => { fetchData(); loadStats(); }, [fetchData, loadStats]);

  const suspiciousReps = useMemo(() => suspiciousRepsFrom(stats), [stats]);
  const highDamageDocs = useMemo(
    () => stats.filter((r) => r.damageRate !== null && r.damageRate >= DAMAGE_RATE_THRESHOLD).length,
    [stats],
  );

  // ── Kolonlar (spec §8 blok 3). TUTAR KOLONU BİLİNÇLİ OLARAK YOK.
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('documentNo', {
      header: 'İrsaliye No',
      cell: (info) => <span className="c360-erp-chip">{info.getValue() || '(taslak)'}</span>,
    }),
    // Sıralama CreatedDate üzerindedir (DB'de DocumentDate kolonu YOK); hücre kesilmiş belgede
    // SentAt'i gösterir — `documentDateOf` ile OData tarih filtresi aynı kuralı paylaşır.
    columnHelper.accessor('createdDate', {
      header: 'Tarih',
      cell: ({ row }) => fmtDate(documentDateOf(row.original)),
    }),
    columnHelper.display({
      id: 'representative', header: 'Plasiyer', enableSorting: false,
      meta: { filterType: 'select' },
      cell: ({ row }) => repName(row.original.representativeId),
    }),
    columnHelper.display({
      id: 'vehicle', header: 'Araç', enableSorting: false,
      meta: { filterType: 'select' },
      cell: ({ row }) => <span style={dst.mono}>{vehicleName(row.original.warehouseId)}</span>,
    }),
    columnHelper.accessor('totalQuantity', {
      header: 'Adet',
      cell: (info) => <span style={{ ...dst.num, fontWeight: 800 }}>{fmtQty(info.getValue())}</span>,
    }),
    columnHelper.accessor('totalDamaged', {
      header: 'Kırık',
      // Kırık, iade edilenin İÇİNDEDİR — Adet'e EKLENMEZ (iş kuralı 3).
      cell: (info) => <span style={dst.num}>{fmtQty(info.getValue())}</span>,
    }),
    // TÜRETİLMİŞ kolon: DB'de saklanmaz → sıralanamaz/filtrelenemez (var olmayan kolona OData
    // kurulamaz). Daraltma "⚠ Yüksek kırık oranı" kısayolundan yapılır.
    columnHelper.display({
      id: 'damageRate', header: 'Kırık %', enableSorting: false,
      cell: ({ row }) => {
        const v = damageRate(row.original.totalQuantity, row.original.totalDamaged);
        const high = v !== null && v >= DAMAGE_RATE_THRESHOLD;
        return (
          <span
            style={{ ...dst.num, ...(high ? dst.danger : dst.ok) }}
            title={high ? `Eşik %${DAMAGE_RATE_THRESHOLD} aşıldı — taşıma sorunu ya da suistimal olabilir.` : undefined}
          >
            {fmtPct(v)}
          </span>
        );
      },
    }),
    columnHelper.accessor('status', {
      header: 'Durum',
      meta: { filterType: 'select' },
      cell: (info) => <StatusBadge tone={statusTone(info.getValue())}>{statusLabel(info.getValue())}</StatusBadge>,
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false, enableSorting: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view"
                  title="Görüntüle" aria-label="Görüntüle"
                  onClick={(e) => { e.stopPropagation(); navigate(`/deposit-returns/${row.original.id}`); }}>
            <ViewIcon />
          </button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [repName, vehicleName, navigate]);

  const sorting = useMemo(
    () => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []),
    [sortField, sortDir],
  );
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('CreatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  // Dışa aktarım FİLTRENİN TAMAMINI alır (sayfayı değil) — ve TUTAR KOLONU YOKTUR.
  const handleExport = async () => {
    const cols = [
      { headerName: 'İrsaliye No', valueGetter: ({ row }) => row.documentNo || '' },
      { headerName: 'Tarih', valueGetter: ({ row }) => fmtDate(documentDateOf(row)) },
      { headerName: 'Plasiyer', valueGetter: ({ row }) => repName(row.representativeId) },
      { headerName: 'Araç', valueGetter: ({ row }) => vehicleName(row.warehouseId) },
      { headerName: 'Adet', valueGetter: ({ row }) => Number(row.totalQuantity || 0) },
      { headerName: 'Kırık', valueGetter: ({ row }) => Number(row.totalDamaged || 0) },
      { headerName: 'Kırık %', valueGetter: ({ row }) => fmtPct(damageRate(row.totalQuantity, row.totalDamaged)) },
      { headerName: 'Durum', valueGetter: ({ row }) => statusLabel(row.status) },
    ];
    try {
      const count = await exportRows(cols, `depozito-iade-irsaliyesi_${new Date().toISOString().slice(0, 10)}`);
      if (!count) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarılamadı');
    }
  };

  const toolbarNode = (
    <div style={dst.filters}>
      <input
        style={s.filterInput}
        type="text"
        placeholder="İrsaliye no ile ara..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && (
        <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>
      )}
      <select style={dst.filterSelect} value={datePreset}
              onChange={(e) => { setDatePreset(e.target.value); setCurrentPage(1); }}>
        {DATE_PRESETS.map((d) => <option key={d.value} value={d.value}>Tarih: {d.label}</option>)}
      </select>
      <span style={{ ...dst.chip, ...(onlyHighDamage ? dst.chipDanger : null) }} role="button"
            title={`Kırık oranı %${DAMAGE_RATE_THRESHOLD} ve üzeri belgeler — taşıma sorunu ya da suistimal işareti.`}
            onClick={() => { setOnlyHighDamage((v) => !v); setCurrentPage(1); }}>
        ⚠ Yüksek kırık oranı
      </span>
      <button style={s.btnSecondary} onClick={refreshAll}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
        <button type="button" title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}
                style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <ExcelIcon size={16} />
        </button>
      </span>
    </div>
  );

  const metaNode = (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <StatusBadge tone="neutral">{totalCount} irsaliye</StatusBadge>
      <StatusBadge tone={highDamageDocs ? 'bad' : 'ok'}>{highDamageDocs} yüksek kırık</StatusBadge>
    </span>
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Depozito İade İrsaliyesi"
        subtitle="Emanetin geri verilmesi — ticari işlem değildir: belgede tutar yoktur, GİB'e gitmez. Karşılığı depozito bakiyesinin azalmasıdır."
        meta={metaNode}
        toolbar={toolbarNode}
        footer={(
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={pageSize}
            rowCount={rows.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      >
        {/* KIRIK ORANI BANDI — tek belge kaza olabilir, tekrar eden örüntü olmaz. */}
        {suspiciousReps.length > 0 && (
          <div style={{ padding: '14px 16px 0' }}>
            <div style={dst.band}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>⚠</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--danger)' }}>
                  {suspiciousReps.length} plasiyer sürekli yüksek kırık bildiriyor (eşik %{DAMAGE_RATE_THRESHOLD})
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', marginTop: 3 }}>
                  {suspiciousReps.slice(0, 3).map((x) => `${repName(x.id)} ${fmtPct(x.rate)} (${x.docs} belge)`).join(' · ')}
                  {suspiciousReps.length > 3 ? ` · +${suspiciousReps.length - 3} plasiyer` : ''}
                  {' — '}ya taşıma sorunu ya da suistimal; sayım isteyin.
                </div>
              </div>
              <button type="button" style={dst.smallBtn} onClick={() => navigate('/deposit-reconciliation')}>
                Mutabakat
              </button>
            </div>
          </div>
        )}

        <div style={{ padding: '14px 16px' }}>
          <C360Table
            table={table}
            columnFilterMeta={columnFilterMeta}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            loading={loading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={(row) => navigate(`/deposit-returns/${row.id}`)}
            emptyText="Depozito iade irsaliyesi bulunamadı"
          />
        </div>
      </ListPageShell>

      <Toast toast={toast} />
    </div>
  );
}

export default DepositReturnsPage;
